import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import { ifDefined } from 'lit/directives/if-defined.js';
import itemCss from './lib-navigation-list-item.css';

@customElement('lib-navigation-list-item')
export class LibNavigationListItem extends LibElement {
    protected override get hostFocusSelector(): string { return 'a[href], button'; }
    static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(itemCss)];

    @property() href = '';
    @property() target = '';
    @property() rel = '';
    @property({ type: Boolean, reflect: true }) active = false;
    @property({ reflect: true }) surface = 'white';
    @property({ type: Boolean, reflect: true }) regular = false;
    @property({ type: Boolean, attribute: 'no-separator', reflect: true }) noSeparator = false;
    @property({ type: Number, attribute: 'badge-number' }) badgeNumber: number | null = null;

    override render() {
        const classes = [
            'lib-nav-list-item',
            this.active ? 'lib-nav-list-item-active' : '',
            this.surface === 'grey' ? 'lib-nav-list-item-grey' : '',
            this.regular ? 'lib-nav-list-item-regular' : '',
            this.noSeparator ? 'lib-nav-list-item-no-separator' : '',
        ].filter(Boolean).join(' ');
        const badgeText = (this.badgeNumber ?? 0) > 9 ? '9+' : this.badgeNumber;
        const content = html`
            ${this.active ? html`<span class="lib-nav-list-item-indicator" aria-hidden="true"></span>` : nothing}
            <slot name="icon"></slot>
            <span class="lib-nav-list-item-container">
                <span class="lib-nav-list-item-text"><slot></slot></span>
                ${this.badgeNumber !== null ? html`<span class="lib-nav-list-item-badge">${badgeText}</span>` : nothing}
                <slot name="chevron"></slot>
            </span>`;
        const ariaCurrent = this.active ? "page" : nothing;
        return this.href
            ? html`<a aria-current=${ariaCurrent} part="item" class=${classes} href=${this.href} target=${ifDefined(this.target || undefined)} rel=${ifDefined(this.rel || undefined)}>${content}</a>`
            : html`<button aria-pressed=${String(this.active)} part="item" class=${classes} type="button">${content}</button>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'lib-navigation-list-item': LibNavigationListItem;
    }
}
